import { Signal, Gauge, Route, AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useSSEQuery } from '@/hooks/useSSEQuery';

export default function FloatingStats() {
  const noSignal = useDashboardStore((s) => s.no_signal);
  const avgSpeed = useDashboardStore((s) => s.avg_speed);
  const enRoute = useDashboardStore((s) => s.en_route);
  const highRisk = useDashboardStore((s) => s.high_risk);
  const isLoaded = useDashboardStore((s) => s.isLoaded);
  const { sendQuery } = useSSEQuery();

  return (
    <>
      {/* Alert badge - top center */}
      {isLoaded && highRisk != null && highRisk > 0 && (
        <button
          onClick={() => sendQuery('Muestrame los vehiculos con riesgo alto')}
          className="absolute top-5 left-1/2 -translate-x-1/2 z-[800] bg-[#dc2626] border border-[#b91c1c] rounded-full px-4 py-2 flex items-center gap-2.5 cursor-pointer hover:-translate-y-0.5 transition-transform animate-fade-in"
          style={{ animation: 'alertPulse 2s infinite' }}
        >
          <AlertTriangle className="text-white" size={14} />
          <span className="text-xs font-bold text-white uppercase tracking-wider">
            Riesgo Alto
          </span>
          <span className="bg-white/20 text-white px-2 py-0.5 rounded-full text-xs font-bold font-mono leading-none">
            {highRisk}
          </span>
        </button>
      )}

      {/* Floating stat cards + search - top right */}
      <div className="absolute top-5 right-5 z-[800] flex flex-col items-end gap-3 w-[180px]">
        <StatCard
          label="Sin Señal > 30m"
          value={isLoaded ? noSignal : undefined}
          icon={Signal}
          variant="alert"
        />
        <StatCard
          label="Velocidad Prom."
          value={isLoaded && avgSpeed != null ? avgSpeed : undefined}
          suffix=" km/h"
          icon={Gauge}
          variant="success"
        />
        <StatCard
          label="En Ruta"
          value={isLoaded ? enRoute : undefined}
          icon={Route}
          variant="default"
        />
      </div>
    </>
  );
}

function StatCard({
  label,
  value,
  suffix = '',
  icon: Icon,
  variant,
}: {
  label: string;
  value?: number | null;
  suffix?: string;
  icon: LucideIcon;
  variant: 'alert' | 'success' | 'default';
}) {
  const valueColorClass = {
    alert: 'text-accent-red',
    success: 'text-accent-green',
    default: 'text-text-primary',
  }[variant];

  return (
    <div className="bg-bg-secondary/90 backdrop-blur-xl border border-border-strong rounded-xl px-4 py-3 w-full shadow-lg hover:border-accent-blue hover:-translate-y-0.5 hover:shadow-xl transition-all cursor-default">
      {/* Header: label + icon */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase text-text-muted font-semibold tracking-wide">
          {label}
        </span>
        <Icon size={14} className="text-text-muted flex-shrink-0" />
      </div>
      {/* Value */}
      <div
        className={`text-2xl font-bold font-mono leading-none ${
          value != null ? valueColorClass : 'text-text-muted text-xl'
        }`}
      >
        {value != null ? `${value}${suffix}` : '...'}
      </div>
    </div>
  );
}

